import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Footer } from "./components/Footer";
import { Nav } from "./components/Nav";
import { ScrollToTop } from "./components/ScrollToTop";
import { FIXTURE_PAGES } from "./config/fixturePages";
import { lazyRoute, lazyRouteWithProps } from "./lib/lazyRoute";
import type { Sport } from "./services/api/schemas";
import {
  RequireAdult,
  RequireAuth,
  RequireEntitlement,
  RequireOnboarded,
} from "./features/auth/guards";
import { About } from "./routes/About";
import { AllFixtures } from "./routes/AllFixtures";
import { AuthCallback } from "./routes/AuthCallback";
import { Home } from "./routes/Home";
import { Landing } from "./routes/Landing";
import { LegalPage } from "./routes/LegalPage";
import { Login } from "./routes/Login";
import { NotFound } from "./routes/NotFound";
import { Pricing } from "./routes/Pricing";
import { Signup } from "./routes/Signup";
import { Team } from "./routes/Team";
import { Unavailable } from "./routes/Unavailable";
import { TermsUpdated } from "./routes/TermsUpdated";
import { Welcome } from "./routes/Welcome";
import { PRIVACY, RESPONSIBLE_GAMBLING, TERMS } from "./content/legal";
import styles from "./App.module.css";

/* Dev-only routes are code-split AND flag-gated, so they can never be LOADED in
   production: DEV is false, so no <Route> referencing them is rendered and the
   dynamic import is never called.

   Their chunks ARE still emitted — this used to read "cannot reach a production
   bundle", which was wrong, and `ls dist/assets` says so. They sit in S3 unfetched.
   That is the accepted cost of the split; what must not happen is mock code
   reaching the EAGER graph, and noMockAuthInProduction asserts exactly that on
   every production build. The build also refuses to compile with mocks on. */
const DEV = import.meta.env.VITE_MOCKS === "on";
const MockCheckout = lazy(() =>
  import("./routes/MockCheckout").then((m) => ({ default: m.MockCheckout })),
);
const Styleguide = lazy(() =>
  import("./routes/Styleguide").then((m) => ({ default: m.Styleguide })),
);
const MockConsole = lazy(() =>
  import("./mocks/console/MockConsole").then((m) => ({
    default: m.MockConsole,
  })),
);

/*
 * The signed-in app, split out of the first download.
 *
 * WHAT THIS FIXES. Everything shipped in one 512 kB chunk, so someone landing
 * on the marketing page — or a crawler reading it, which starts mattering the
 * day VITE_INDEXABLE flips on at the apex launch — downloaded the chat client,
 * the season board and the prop tables before the hero rendered. src/features/
 * chat is 272 kB of source on its own and is unreachable until you are signed
 * in, onboarded, adult-verified AND entitled. Nobody's first page view needs it.
 *
 * WHERE THE LINE IS. Split at the authenticated boundary, not per-route.
 * Everything a signed-out visitor can reach stays eager — landing, pricing,
 * about, team, login, signup, the legal documents, 404 — because those are the
 * pages where a suspense gap is a blank first impression and the whole point is
 * that they arrive fast. Everything behind RequireAuth is lazy, because by then
 * the visitor has committed and a chunk fetch rides alongside the API calls
 * those pages were going to make anyway.
 *
 * Home stays EAGER despite `/app` being authenticated-shaped: it is where
 * sign-in lands by default, so it is the one signed-in page reached by a
 * browser that has just been handed a fresh bundle, and making it wait for a
 * second round trip would put the gap exactly where it is most visible. It
 * is also, since 2026-09-07, the page mounted at `/` for every anonymous
 * visitor and crawler — doubly a reason it cannot sit behind a chunk fetch.
 *
 * ⚠️ THIS USED TO BE Directory, AND THE COMMENT SAID SO. THE DECISION (Ned,
 * 2026-09-07) moved the landing spot from the model directory to the free
 * fixtures list — Home now carries the reasoning above, and Directory (the
 * model grid + chat picker, "Your models") moved to `/app/models`, one hop
 * further from a fresh bundle and so, like every other authenticated page,
 * lazy.
 *
 * The Suspense boundary they fall into is the one already wrapping <Routes>.
 */
const Account = lazyRoute(() => import("./routes/Account").then((m) => ({ default: m.Account })));
const BillingReturn = lazyRoute(() =>
  import("./routes/BillingReturn").then((m) => ({ default: m.BillingReturn })),
);
const Board = lazyRoute(() => import("./routes/Board").then((m) => ({ default: m.Board })));
const Chat = lazyRoute(() => import("./routes/Chat").then((m) => ({ default: m.Chat })));
const Directory = lazyRoute(() =>
  import("./routes/Directory").then((m) => ({ default: m.Directory })),
);
const Insights = lazyRoute(() =>
  import("./routes/Insights").then((m) => ({ default: m.Insights })),
);
const Fixtures = lazyRoute(() => import("./routes/Fixtures").then((m) => ({ default: m.Fixtures })));
const Player = lazyRoute(() => import("./routes/Player").then((m) => ({ default: m.Player })));
const Props = lazyRoute(() => import("./routes/Props").then((m) => ({ default: m.Props })));
const Multi = lazyRoute(() => import("./routes/Multi").then((m) => ({ default: m.Multi })));
const FootballMulti = lazyRoute(() =>
  import("./routes/FootballMulti").then((m) => ({ default: m.FootballMulti })),
);
/* The football board is a SEPARATE route, not a sport flag on the one above.
   The mechanics are shared by import; what differs is what the page may
   claim — the NBA footnote "only calibrated lines appear" is false of the
   football build. See FootballProps.tsx. */
const FootballProps = lazyRoute(() =>
  import("./routes/FootballProps").then((m) => ({ default: m.FootballProps })),
);
/* One level in from the fixture list — the same relationship /app/player/:name
   has to the player-form table. ONE component for every sport that has a page;
   which sports those are, and where each one lives, is `config/fixturePages.ts`.
   See FixturePageRoute.tsx / FixturePage.tsx. */
const FixturePageRoute = lazyRouteWithProps<{ sport: Sport }>(() =>
  import("./routes/FixturePageRoute").then((m) => ({ default: m.FixturePageRoute })),
);
const Season = lazyRoute(() => import("./routes/Season").then((m) => ({ default: m.Season })));
/* Franco's room. ONE route for all four codes, unlike the props and multi
   boards above, and the difference is real rather than a change of heart:
   those two pages differ in what they may CLAIM, so they are separate files.
   This one differs in what each code's GAME IS — a formation for FPL, a
   thirty-man list for the NBA, a documented absence for the AFL — and that
   is data, held in features/fantasy/rules.ts, not four copies of a page. */
const Fantasy = lazyRoute(() => import("./routes/Fantasy").then((m) => ({ default: m.Fantasy })));

export function App() {
  return (
    <Suspense fallback={null}>
      {/* Above the Routes, so it sees every navigation including the ones into
          and out of the bare mock-checkout route. */}
      <ScrollToTop />
      <Routes>
        {/* Rendered BARE — no KEV nav, no KEV footer. It stands in for a
            cross-origin Stripe page, and wrapping it in our chrome would hide
            the fact that the user genuinely leaves the site and comes back. */}
        {DEV && <Route path="/mock-checkout" element={<MockCheckout />} />}
        <Route path="*" element={<Shell />} />
      </Routes>
    </Suspense>
  );
}

function Shell() {
  return (
    <div className={styles.shell}>
      {/* First thing in the tab order, invisible until focused. Every page puts
          the same nav and the same long compliance footer around the content,
          so without this a keyboard or screen-reader user tabs through the
          identical four nav items on every single page before reaching anything
          that differs. */}
      <a href="#main" className={styles.skipLink}>
        Skip to content
      </a>
      <Nav />
      {/* tabIndex={-1} makes this focusable by script but keeps it out of the
          tab order — it is what lets the skip link and the footer's Back to top
          actually move focus, rather than only moving the viewport and leaving
          the next Tab where it was. */}
      <main className={styles.main} id="main" tabIndex={-1}>
        {/* Wraps the ROUTES only, not the Shell. A render error inside a page
            must not take the nav and the footer down with it — the footer
            carries the required compliance line and the support numbers, and
            those are the last things that should vanish when something breaks. */}
        <ErrorBoundary>
          <Suspense fallback={null}>
            <Routes>
              {/* THE LANDING SPOT (Ned, 2026-09-07, kev-docs/kev-landing-and-
                  seo-plan.md, the doc that governs — see the note on /app
                  below, which this duplicates deliberately). "The landing
                  page to be all the fixtures of upcoming sports" meant `/`
                  itself, not `/app`: a stranger who opens the real domain
                  has to land on the fixture list, not a marketing page
                  describing a product they have not been shown yet.

                  PUBLIC — no RequireAuth, for the identical reason /app has
                  none: RequireOnboarded and RequireAdult both no-op for an
                  anonymous visitor (see their own source) and still do their
                  real job for someone signed in who has not cleared one. */}
              <Route
                path="/"
                element={
                  <RequireOnboarded>
                    <RequireAdult>
                      <Home />
                    </RequireAdult>
                  </RequireOnboarded>
                }
              />
              {/* The marketing page, displaced from `/` by the fixture list
                  above. Not deleted — every internal link and CTA that used
                  to mean "the marketing page at /" now means this path;
                  `/#the-idea` and `/#the-models` moved with it (see Hero.tsx
                  and destinations.ts). Named distinctly from `/about`: About
                  is "why KEV exists, what an answer contains, who built it"
                  for a reader who kept scrolling — this is the pitch itself. */}
              {/* "See more" from the landing spot (Ned, 2026-09-07, same doc
                  as `/` above) — the same anonymous fixture list, a bigger
                  window. Same guard composition as `/` for the same reason:
                  RequireOnboarded/RequireAdult no-op for an anonymous
                  visitor and still gate someone signed in who hasn't
                  cleared one. See routes/AllFixtures.tsx and
                  config/routes.ts's matching PUBLIC_ROUTES entry. */}
              <Route
                path="/fixtures"
                element={
                  <RequireOnboarded>
                    <RequireAdult>
                      <AllFixtures />
                    </RequireAdult>
                  </RequireOnboarded>
                }
              />
              <Route path="/why-kev" element={<Landing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/about" element={<About />} />
              <Route path="/team" element={<Team />} />
              {/* `/mail/confirmed` was here, and the comment above it said the
                  double opt-in decision was open and named this route and
                  MailConfirmed.tsx as what to remove if it went the other way.
                  It went the other way on 2026-08-28: the mailing list is gone
                  and the free account is the only funnel, so both are deleted.

                  NO REDIRECT AND NO 410 IS NEEDED, which is worth stating
                  because deleting a route usually earns one. Nothing ever sent
                  a link to this path — `handler_waitlist.py` is single opt-in
                  and sends no email at all — so there is no message in anyone's
                  inbox that now lands on /404. It was reachable only by typing
                  it. */}

              {/* The free board: sign-in required BY DESIGN. It used to be put
                as "the free tier is the mailing list", which stopped being true
                on 2026-08-28 when the list went. The design is unchanged and
                the reason is now simpler — a free account is what we ask for
                instead of an address, and this is what it opens. Adult gate
                too: it publishes tips. */}
              <Route
                path="/board"
                element={
                  <RequireAuth>
                    <RequireOnboarded>
                      <RequireAdult>
                        <Board />
                      </RequireAdult>
                    </RequireOnboarded>
                  </RequireAuth>
                }
              />
              <Route path="/login" element={<Login />} />
              {/* Its own page since 2026-08-07, not a mode flag on Login. It
                  creates the account itself (POST /signup) because Cognito's
                  own form is closed — see Signup.tsx. */}
              <Route path="/signup" element={<Signup />} />
              {/* Sits OUTSIDE RequireAuth — this is the page that MAKES you
                authenticated, so guarding it would loop it against itself. */}
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route
                path="/legal/terms"
                element={<LegalPage doc={TERMS} page="terms" />}
              />
              <Route
                path="/legal/privacy"
                element={<LegalPage doc={PRIVACY} page="privacy" />}
              />
              <Route
                path="/legal/responsible-gambling"
                element={
                  <LegalPage
                    doc={RESPONSIBLE_GAMBLING}
                    page="responsibleGambling"
                  />
                }
              />

              {/* RequireAuth ONLY — the same composition trick /signup/age used,
                and for the same reason: this is the page that satisfies
                RequireOnboarded and RequireAdult, so wrapping it in either would
                make it redirect to itself forever.

                It replaced /signup/age on 2026-08-07. One gate, not two: it asks
                for a date of birth the server checks rather than a tick the
                server believed, and it takes the same consent /signup does — for
                accounts that reached Cognito's own sign-up form without ever
                loading /signup. */}
              <Route
                path="/welcome"
                element={
                  <RequireAuth>
                    <Welcome />
                  </RequireAuth>
                }
              />

              {/* Re-acceptance, and RequireAuth ONLY for the same reason as
                /welcome directly above: this page is what satisfies
                RequireOnboarded, so wrapping it in that guard would redirect it
                to itself forever.

                Separate from /welcome because /welcome is written for someone
                who has just arrived — "Finish setting up", "this is the only
                time you'll be asked", four empty required fields including a
                date of birth /me deliberately never returns. Sending a paying
                customer there to re-accept a document meant retyping a DOB from
                memory, and a mistyped year answers 403 under_18 and locks them
                out. See TermsUpdated.tsx. */}
              <Route
                path="/terms-updated"
                element={
                  <RequireAuth>
                    <TermsUpdated />
                  </RequireAuth>
                }
              />

              {/* KEPT WORKING, DELIBERATELY DUPLICATING `/` ABOVE (moved
                  there 2026-09-07 — see that route's own note). `/app` was
                  the fixture list's first home for one day; every deep link,
                  bookmark and CTA already built against it — the football
                  fixture pages' up-nav (`upNav.ts`), `RequireEntitlement`'s
                  `/app?unavailable=`/`/app?unknown=1` bounces, the Nav
                  brand-adjacent CTAs — keeps working unchanged rather than
                  taking a redirect hop, and `/app` stays in CRAWLER_DISALLOW
                  so a crawler never indexes it as a second copy of `/`.

                  Same guard composition as `/` for the same reason:
                  RequireOnboarded and RequireAdult both no-op for an
                  anonymous visitor (see their own source) and still do their
                  real job for someone signed in who has not cleared one. */}
              <Route
                path="/app"
                element={
                  <RequireOnboarded>
                    <RequireAdult>
                      <Home />
                    </RequireAdult>
                  </RequireOnboarded>
                }
              />
              {/* A bare fixture-list URL with no key — not a 404. Someone
                  reaches this by hand-trimming a fixture-page URL back to
                  what looks like its list, and the list lives at `/`, not
                  here. ONE PER SPORT, off the same table the pages themselves
                  come from, so a sport cannot get a page without this
                  courtesy. */}
              {FIXTURE_PAGES.map((source) => (
                <Route
                  key={source.sport}
                  path={source.path}
                  element={<Navigate to="/" replace />}
                />
              ))}
              {/* The model directory / chat picker — "Your models". Moved off
                  `/app` the same day Home took it over; every existing "Your
                  models" CTA (Pricing, Account, Season's own up-link at the
                  top of its hierarchy) was repointed here rather than left
                  naming a page that no longer shows models. */}
              <Route
                path="/app/models"
                element={
                  <RequireAuth>
                    <RequireOnboarded>
                      <RequireAdult>
                        <Directory />
                      </RequireAdult>
                    </RequireOnboarded>
                  </RequireAuth>
                }
              />
              {/* Insights — the Premium tool menu, its own top-level nav item
                  beside Fixtures. A menu over pages that already exist (see
                  Insights.tsx); no new gate, no new tools. */}
              <Route
                path="/app/insights"
                element={
                  <RequireAuth>
                    <RequireOnboarded>
                      <RequireAdult>
                        <Insights />
                      </RequireAdult>
                    </RequireOnboarded>
                  </RequireAuth>
                }
              />
              {/* Premium season futures. RequireAdult like /board and for the
                  same reason — it publishes tips-adjacent output. No tier guard:
                  the SERVER refuses a free-tier caller with 403 not_entitled and
                  the page renders the upsell where the data would be, which
                  keeps one access decision in one place. See Season.tsx. */}
              {/* ⚠️ THE PARAMS ARE OPTIONAL, AND BARE /app/season MUST KEEP
                  WORKING. It is the entry point the "Premium Insights
                  Dashboard" button links to, and it is deliberately the state
                  with no sport chosen — see the note in Season.tsx about a
                  default sport being the bug rather than the fix. The optional
                  segments only let a CHOSEN sport and board be addressed, so
                  Back walks between boards and a board can be linked. */}
              <Route
                path="/app/season/:sport?/:board?"
                element={
                  <RequireAuth>
                    <RequireOnboarded>
                      <RequireAdult>
                        <Season />
                      </RequireAdult>
                    </RequireOnboarded>
                  </RequireAuth>
                }
              />
              {/* The Premium prop board. Same composition as /app/season and
                  for the same reasons: RequireAdult because it publishes priced
                  lines, and NO tier guard — the server refuses a caller without
                  a bundle with 403 not_entitled and the page renders the upsell
                  where the board would be. See Props.tsx. */}
              {/* The Premium fixture board: one match, every cleared market,
                  each with a fair price. Composed exactly like /app/season and
                  gated the same way — the SERVER refuses a caller without the
                  bundle with 403 not_entitled and the page renders the upsell
                  where the markets would be. No tier guard here: the UI is not
                  a control. */}
              {/* Franco's fantasy rooms. Composed like /app/season and gated the
                  same way — which is to say the UI is not a control. Nothing on
                  this page is bundle-gated: the rules of a game are not ours to
                  withhold, and the saved-squad endpoint behind it carries no
                  subscription check at all, deliberately, so a lapsed subscriber
                  keeps the list of what they saved. What a subscription buys is
                  the conversation, so the page turns its prompts into the offer
                  for a reader who cannot ask. See Fantasy.tsx.

                  ⚠️ THE PARAM IS OPTIONAL AND BARE /app/fantasy MUST KEEP
                  WORKING, selecting nothing — the same rule /app/season follows,
                  for the same reason: a reader who subscribes to one Franco and
                  lands in another one's room has been told something false about
                  what they own. */}
              <Route
                path="/app/fantasy/:sport?"
                element={
                  <RequireAuth>
                    <RequireOnboarded>
                      <RequireAdult>
                        <Fantasy />
                      </RequireAdult>
                    </RequireOnboarded>
                  </RequireAuth>
                }
              />
              <Route
                path="/app/fixtures"
                element={
                  <RequireAuth>
                    <RequireOnboarded>
                      <RequireAdult>
                        <Fixtures />
                      </RequireAdult>
                    </RequireOnboarded>
                  </RequireAuth>
                }
              />
              {/* The per-fixture page: Kev's full case for ONE match, one
                  level in from the fixture list above. PUBLIC, same reason
                  `/app` is above it and the SAME REMOVAL — RequireOnboarded
                  and RequireAdult stay because they no-op for a signed-out
                  visitor; only RequireAuth actually blocked one and only it
                  is gone.

                  `GET /football/fixture/{key}` is still Premium-gated
                  server-side, and `GET /teaser?artifact=fixtures-free` is
                  still an account-tier fetch the page only trusts once
                  `status === "authenticated"` — see FixturePage.tsx's own
                  note on the three passes (anonymous, account, Premium). No
                  route guard decides any of that; the page renders each
                  tier's upsell in place of what it cannot show yet.

                  ⚠️ REGISTERED BY MAPPING OVER `FIXTURE_PAGES`, NOT WRITTEN
                  OUT PER SPORT. That table is also what `hrefFor` reads to
                  decide whether a fixture row is a link at all, so a sport
                  cannot have a link with no route or a route nothing links to
                  — which is the exact half-state that left the NFL's sixteen
                  built pages unreachable. Adding a sport is one row there. */}
              {FIXTURE_PAGES.map((source) => (
                <Route
                  key={source.sport}
                  path={`${source.path}/:key`}
                  element={
                    <RequireOnboarded>
                      <RequireAdult>
                        <FixturePageRoute sport={source.sport} />
                      </RequireAdult>
                    </RequireOnboarded>
                  }
                />
              ))}
              <Route
                path="/app/props"
                element={
                  <RequireAuth>
                    <RequireOnboarded>
                      <RequireAdult>
                        <Props />
                      </RequireAdult>
                    </RequireOnboarded>
                  </RequireAuth>
                }
              />
              {/* The Multi Builder. Composed exactly like /app/props above and
                  gated the same way — `multi_menu` is TIER_GATED on the Lambda,
                  so a caller without a bundle gets 403 not_entitled and the page
                  renders the upsell where the builder would be. No tier guard
                  here: the UI is not a control.

                  RequireAdult for the reason every priced surface carries it —
                  this one publishes a price on a combination, which is the most
                  bet-shaped thing in the product. */}
              <Route
                path="/app/multi"
                element={
                  <RequireAuth>
                    <RequireOnboarded>
                      <RequireAdult>
                        <Multi />
                      </RequireAdult>
                    </RequireOnboarded>
                  </RequireAuth>
                }
              />
              {/* The Premier League Multi Builder. Composed exactly like
                  /app/multi and gated the same way — `multi_menu` is Premium on
                  the football runner, so a caller without a bundle gets 403 and
                  the page renders the upsell. The MODEL behind it is different:
                  match markets combine exactly off one scoreline grid, and at
                  most one player leg may join them. See FootballMulti.tsx. */}
              <Route
                path="/app/football/multi"
                element={
                  <RequireAuth>
                    <RequireOnboarded>
                      <RequireAdult>
                        <FootballMulti />
                      </RequireAdult>
                    </RequireOnboarded>
                  </RequireAuth>
                }
              />
              {/* The Premier League prop board. Composed exactly like /app/props
                  above and gated the same way — Premium on the runner AND behind
                  require_bundle_tier in handler_chat, so a caller without a
                  bundle gets 403 and the page renders the upsell. No tier guard
                  here: the UI is not a control. */}
              <Route
                path="/app/football/props"
                element={
                  <RequireAuth>
                    <RequireOnboarded>
                      <RequireAdult>
                        <FootballProps />
                      </RequireAdult>
                    </RequireOnboarded>
                  </RequireAuth>
                }
              />
              {/* The deep dive, reached by clicking a row on the season board's
                  player table. Composed exactly like /app/season and /app/props —
                  RequireAdult because it is a betting-analytics surface, and NO
                  tier guard: `player_games` is Premium on the Lambda and a caller
                  without it gets 403, which the page renders as the upsell. */}
              <Route
                path="/app/player/:name"
                element={
                  <RequireAuth>
                    <RequireOnboarded>
                      <RequireAdult>
                        <Player />
                      </RequireAdult>
                    </RequireOnboarded>
                  </RequireAuth>
                }
              />
              <Route
                path="/app/account"
                element={
                  <RequireAuth>
                    <RequireOnboarded>
                      <RequireAdult>
                        <Account />
                      </RequireAdult>
                    </RequireOnboarded>
                  </RequireAuth>
                }
              />
              {/* No RequireAdult: bouncing someone off their own payment
                confirmation would be a bad way to learn their DOB flag is stale. */}
              <Route
                path="/app/billing/return"
                element={
                  <RequireAuth>
                    <BillingReturn />
                  </RequireAuth>
                }
              />
              <Route
                path="/app/m/:modelId"
                element={
                  <RequireAuth>
                    <RequireOnboarded>
                      <RequireAdult>
                        <RequireEntitlement>
                          <Chat />
                        </RequireEntitlement>
                      </RequireAdult>
                    </RequireOnboarded>
                  </RequireAuth>
                }
              />

              {/* Where the AustraliaOnly WAF rule sends non-AU document requests.
                Public and guard-free on purpose: it has to render for someone
                with no account who cannot reach /api to make one. */}
              <Route path="/unavailable" element={<Unavailable />} />

              {DEV && <Route path="/dev/styleguide" element={<Styleguide />} />}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
      {DEV && (
        <Suspense fallback={null}>
          <MockConsole />
        </Suspense>
      )}
    </div>
  );
}
